import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuthContext } from '../../context/auth';
import { PolicyData } from '../../context/policy';
import {
	PremiumReportData,
	withPremiumReportContext,
} from '../../context/premiumReport';
import { useRouteMatch } from '../../util/useRouteMatch';

import TreeNode, {
	TreeData,
	TreeItemData,
} from '../layouts/SidePanel/TreeNode';

interface Props {
	policyData: PolicyData;
	premiumReportData: PremiumReportData;
}

export interface PremiumReportRoutes {
	INVOICES: string;
	INVOICE: string;
}

export const PREMIUM_REPORT_ROUTES: PremiumReportRoutes = {
	INVOICES: '/policy/:policyId/premiumReports/:premiumReportId/invoices',
	INVOICE:
		'/policy/:policyId/premiumReports/:premiumReportId/invoices/:invoiceId',
};

const PremiumReportSidebar = (props: Props) => {
	const params = useParams();
	const authData = useAuthContext();
	const { hasPermission } = authData;
	const { pathname } = useLocation();
	const { policyId, premiumReportId } = params;
	const { premiumReportData } = props;
	// react-router v6 support for nested routes runs low when using several nested <Routes /> components
	// At this level, the params object deeper than the first level is not populated and is stored in params['*']
	// E.g., /premiumReports/123/invoices/456 will have params['*'] = 'invoices/456'
	const invoiceParams = params['*']?.split('/');
	const invoiceIdFromParams = invoiceParams?.[1];
	const { premiumReport, replaceMode } = premiumReportData;

	if (!premiumReport) {
		return <CircularProgress />;
	}
	const routeName = pathname.split('/').pop();
	const routeMatch = useRouteMatch(
		Object.keys(PREMIUM_REPORT_ROUTES).map(
			(key) => PREMIUM_REPORT_ROUTES[key as keyof PremiumReportRoutes],
		),
	);
	const premiumReportRouteName = routeMatch?.pattern?.path || '';

	const getInitialExpandedItems = () => {
		const ids = [] as string[];
		if (invoiceIdFromParams) {
			ids.push('Invoices');
		}
		return ids;
	};
	const [itemsExpanded, setItemsExpanded] = useState([]);

	useEffect(() => {
		const initialIds = getInitialExpandedItems();
		setItemsExpanded(initialIds);
	}, [params.invoiceId]);

	const premiumReportLink = `/policy/${policyId}/premiumReports/${premiumReportId}`;
	const detailsLink = `${premiumReportLink}/details`;
	const priceBreakdownLink = `${premiumReportLink}/pricing`;
	const documentsLink = `${premiumReportLink}/documents`;
	const invoicesLink = `${premiumReportLink}/invoices`;

	const getRootItem = () => {
		let children = ['Details'];

		if (premiumReport.state !== 'draft' && !replaceMode) {
			children.push('Documents');

			if (hasPermission('invoice.fetch')) {
				children.push('Invoices');
			}

			children.push('Price Breakdown');
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		};
	};
	const getItems = () => {
		const isDetailsSelected =
			!!params.policyId && (routeName === 'details' || routeName === 'auxData');
		const items: TreeItemData = {
			root: getRootItem(),
			Details: {
				id: 'Details',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Details',
					link: detailsLink,
					selected: isDetailsSelected,
				},
			},
			'Price Breakdown': {
				id: 'Price Breakdown',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Price Breakdown',
					link: priceBreakdownLink,
					selected: routeName === 'pricing',
				},
			},
			Documents: {
				id: 'Documents',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Documents',
					link: documentsLink,
					selected: routeName === 'documents',
				},
			},
			Invoices: {
				id: 'Invoices',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Invoices',
					link: invoicesLink,
					selected:
						premiumReportRouteName === PREMIUM_REPORT_ROUTES.INVOICES ||
						premiumReportRouteName === PREMIUM_REPORT_ROUTES.INVOICE,
				},
			},
		};

		return {
			...items,
		};
	};

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: getItems(),
		};

		return sidePanelData;
	};
	const tree = initializeTree();

	return <TreeNode treeData={tree} defaultExpanded={itemsExpanded} />;
};

export default withPremiumReportContext<Props>(PremiumReportSidebar);
